import { Task } from "@lit/task";
import {
    EmployeePayrollDataDTO,
    EmploymentType,
    ErrorCode,
    INVALID_IBAN_MESSAGE,
    isValidIban,
    UpdateEmployeePayrollDataParams,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { alert, confirm } from "../../../elements/alert-dialog";
import "../../../elements/scroller";
import { app } from "../../../init";
import { routeProperty, Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";

export type ContractDerivedData = {
    currentContractId?: number;
    firstContractId?: number;
    latestContractId?: number;
    employmentType?: EmploymentType;
    employmentStart?: DateString;
    employmentEnd?: DateString;
    paymentType?: string;
    monthlySalary?: number;
    hourlyRate?: number;
    hoursPerWeek?: number;
};

@customElement("ptc-employees-payroll-data-single")
export class EmployeesPayrollDataSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<employeeId>\d+)\/payroll-data?/;

    get routeTitle() {
        return `Lohndaten: ${this._employee && this._employee.name}`;
    }

    @routeProperty({ type: Number, arg: "employeeId" })
    private _employeeId: number;

    @state()
    private _loading: boolean = false;

    @state()
    private _payrollData: EmployeePayrollDataDTO | undefined;

    @state()
    private _contractDerivedData: ContractDerivedData | undefined;

    private _payrollDataTask = new Task(this, {
        task: async () => {
            let payrollData: EmployeePayrollDataDTO | undefined;
            let contractDerivedData: ContractDerivedData | undefined;
            if (!this._employeeId) {
                return [undefined, undefined];
            }
            try {
                payrollData = await app.api.getEmployeePayrollData({
                    employeeId: this._employeeId,
                });

                const currentContract = this._employee.currentContract;

                const contractSalaryType = currentContract?.salaries?.[0]?.type;

                contractDerivedData = {
                    currentContractId: currentContract?.id,
                    firstContractId: this._employee.firstContract?.id,
                    latestContractId: this._employee.latestContract?.id,
                    employmentType: currentContract?.employmentType,
                    employmentStart: this._employee.firstContract?.start,
                    employmentEnd: this._employee.latestContract?.end || undefined,
                    paymentType: contractSalaryType,
                    monthlySalary:
                        contractSalaryType === "monthly" ? currentContract?.salaries?.[0]?.amount : undefined,
                    hourlyRate: contractSalaryType === "hourly" ? currentContract?.salaries?.[0]?.amount : undefined,
                    hoursPerWeek: currentContract?.hoursPerWeek,
                };
            } catch (error) {
                // If no payroll data exists yet, initialize with empty data
                if (error?.code === ErrorCode.NOT_FOUND) {
                    payrollData = new EmployeePayrollDataDTO({
                        employeeId: this._employeeId,
                        mainJob: false,
                    });
                } else {
                    console.error("Fehler beim Laden der Lohndaten:", error);
                    void alert("Die Lohndaten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.", {
                        title: "Fehler",
                        type: "warning",
                    });
                }
            }

            this._payrollData = payrollData;
            this._contractDerivedData = contractDerivedData;

            return [payrollData, contractDerivedData];
        },
        args: () => [this._employeeId],
    });

    private get _employee() {
        return app.getEmployee(this._employeeId)!;
    }

    static styles = [
        shared,
        css`
            #payrollDataForm {
                max-width: 60em;
            }
        `,
    ];

    private async _handleFormSubmit(event: CustomEvent<UpdateEmployeePayrollDataParams>) {
        const formData = event.detail;

        // Validate the form data
        if (!formData) {
            return;
        }

        if (formData.bankDetails?.iban && !isValidIban(formData.bankDetails.iban)) {
            void alert(INVALID_IBAN_MESSAGE, { title: "Fehler", type: "warning" });
            return;
        }

        // Show confirmation for critical changes
        const hasMainJobChanged = this._payrollData?.mainJob !== formData.mainJob;

        if (
            hasMainJobChanged &&
            !(await confirm(
                html`Sie sind dabei, wichtige Lohndaten zu ändern. Diese Änderungen können Auswirkungen auf die
                Gehaltsabrechnung haben. Möchten Sie fortfahren?`,
                "Bestätigen",
                "Abbrechen",
                { title: "Lohndaten Ändern" }
            ))
        ) {
            return;
        }

        await this._update(formData);
    }

    private _handleFormCancel() {
        // Reset form to last saved _payrollData by triggering re-render
        this.requestUpdate();
    }

    private async _update(data: Partial<UpdateEmployeePayrollDataParams>) {
        this._loading = true;
        try {
            // Update payroll data via API
            const updatedData = await app.api.updateEmployeePayrollData(
                new UpdateEmployeePayrollDataParams({
                    employeeId: this._employeeId,
                    ...data,
                })
            );

            this._payrollData = updatedData;

            void alert("Die Lohndaten wurden erfolgreich gespeichert.", {
                title: "Erfolg",
            });
        } catch (error) {
            console.error("Fehler beim Speichern der Lohndaten:", error);
            void alert(
                error?.message ||
                    "Die Lohndaten konnten nicht gespeichert werden. Bitte versuchen Sie es später erneut.",
                {
                    title: "Fehler",
                    type: "warning",
                }
            );
        } finally {
            this._loading = false;
        }
    }

    private _renderForm() {
        const employmentType = this._contractDerivedData?.employmentType;

        switch (employmentType) {
            case EmploymentType.Marginal:
                return html`
                    <ptc-ui-marginal-employment-form
                        .employeeId=${this._employeeId}
                        .payrollData=${this._payrollData}
                        @form-submit=${this._handleFormSubmit}
                        @form-cancel=${this._handleFormCancel}
                    ></ptc-ui-marginal-employment-form>
                `;
            case EmploymentType.ShortTerm:
                return html`
                    <ptc-ui-short-term-employment-form
                        .employeeId=${this._employeeId}
                        .payrollData=${this._payrollData}
                        @form-submit=${this._handleFormSubmit}
                        @form-cancel=${this._handleFormCancel}
                    ></ptc-ui-short-term-employment-form>
                `;
            case EmploymentType.WorkingStudent:
                return html`
                    <ptc-ui-working-student-form
                        .employeeId=${this._employeeId}
                        .payrollData=${this._payrollData}
                        @form-submit=${this._handleFormSubmit}
                        @form-cancel=${this._handleFormCancel}
                    ></ptc-ui-working-student-form>
                `;
            case EmploymentType.Regular:
            case EmploymentType.Independent:
            case EmploymentType.Trainee:
            case EmploymentType.MidiJob:
            case EmploymentType.DualStudent:
            case EmploymentType.Intern:
            case undefined:
                return html`
                    <ptc-ui-regular-employment-form
                        .employeeId=${this._employeeId}
                        .payrollData=${this._payrollData}
                        @form-submit=${this._handleFormSubmit}
                        @form-cancel=${this._handleFormCancel}
                    ></ptc-ui-regular-employment-form>
                `;
        }
    }

    render() {
        return html`${this._payrollDataTask.render({
                initial: () => html``,
                pending: () =>
                    html`<div class="horizontal centering layout double-padded">
                        <ptc-ui-spinner active style="font-size: 0.53em"></ptc-ui-spinner>
                    </div>`,
                error: () =>
                    html`<div class="double-padded text-centering">Lohndaten konnten nicht geladen werden!</div>`,
                complete: () => html`
                    <div class="core_data fullbleed">
                        <div class="vertical layout fit-vertically">
                            <ptc-scroller class="stretch"> ${this._renderForm()} </ptc-scroller>
                        </div>
                    </div>
                `,
            })}

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>`;
    }
}
